import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import {
  AccountResponse,
  CareerRoleActivationStatus,
  CareerRoleAllowedAccountDto,
  CareerRoleWebListItemResponse,
  RoleSpecializationFullResponse
} from '../../../../models';
import { CareerRoleAdminActionsService } from '../shared/career-role-admin-actions.service';
import { CareerRoleFullPictureStore } from '../shared/career-role-full-picture.store';

@Component({
  selector: 'app-overview-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatMenuModule,
    MatProgressBarModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatTooltipModule
  ],
  templateUrl: './overview-tab.component.html',
  styleUrl: './overview-tab.component.scss'
})
export class OverviewTabComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(CsrWebApiService);
  private readonly actions = inject(CareerRoleAdminActionsService);
  private readonly pictureStore = inject(CareerRoleFullPictureStore);
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);

  private readonly slugSignal = signal('');
  readonly roleState = computed(() => {
    const slug = this.slugSignal();
    if (!slug) {
      return null;
    }
    return this.pictureStore.getState(slug) ?? null;
  });

  readonly actionBusy = signal(false);
  listItem: CareerRoleWebListItemResponse | null = null;
  accountOptions: AccountResponse[] = [];
  roleAccountDraft: string[] = [];
  specAccountDrafts: Record<string, string[]> = {};
  customizingSpec = new Set<string>();

  readonly specForm = this.fb.nonNullable.group({
    name: ['', Validators.required],
    slug: ['']
  });

  get slug(): string {
    return this.slugSignal();
  }

  get picture() {
    return this.roleState()?.picture ?? null;
  }

  get loading(): boolean {
    return this.roleState()?.loading ?? false;
  }

  get refreshing(): boolean {
    return this.roleState()?.refreshing ?? false;
  }

  get isBusy(): boolean {
    return this.loading || this.actionBusy();
  }

  ngOnInit(): void {
    const slug = this.route.parent?.snapshot.paramMap.get('slug') ?? '';
    this.slugSignal.set(slug);
    void this.reload();
  }

  async reload(force = false): Promise<void> {
    if (!this.slug) {
      return;
    }
    this.actionBusy.set(true);
    try {
      await Promise.all([
        this.pictureStore.load(this.slug, { force, background: !force && !!this.picture }),
        this.reloadListItem(),
        this.loadAccountOptions()
      ]);
      this.syncAccountDraftsFromPicture();
    } finally {
      this.actionBusy.set(false);
    }
  }

  private async reloadListItem(): Promise<void> {
    this.listItem = await firstValueFrom(this.api.getCareerRoleOverview(this.slug));
  }

  private async loadAccountOptions(): Promise<void> {
    if (this.accountOptions.length > 0) {
      return;
    }
    const page = await firstValueFrom(this.api.listAccounts(1, 200, undefined, undefined, 'active'));
    this.accountOptions = page.data ?? [];
  }

  private syncAccountDraftsFromPicture(): void {
    const role = this.picture;
    if (!role) {
      return;
    }
    this.roleAccountDraft = [...(role.allowedAccountIds ?? [])];
    this.specAccountDrafts = {};
    this.customizingSpec.clear();
    for (const spec of role.specializations ?? []) {
      this.specAccountDrafts[spec.slug] = [...(spec.allowedAccountIds ?? [])];
      if ((spec.allowedAccountIds?.length ?? 0) > 0) {
        this.customizingSpec.add(spec.slug);
      }
    }
  }

  statusLabel(): string {
    return this.isEngaged() ? 'active' : 'inactive';
  }

  isEngaged(): boolean {
    const s = this.picture?.activationStatus;
    return s === 'active' || s === 'activating' || !!this.picture?.active;
  }

  specStatus(spec: RoleSpecializationFullResponse): CareerRoleActivationStatus {
    return spec.activationStatus ?? (spec.active ? 'active' : 'inactive');
  }

  isSpecEngaged(spec: RoleSpecializationFullResponse): boolean {
    const status = this.specStatus(spec);
    return status === 'active' || status === 'activating' || spec.active;
  }

  isSpecMigratable(spec: RoleSpecializationFullResponse): boolean {
    return this.specStatus(spec) === 'active' || spec.active;
  }

  specStatusLabel(spec: RoleSpecializationFullResponse): string {
    return this.isSpecEngaged(spec) ? 'active' : 'inactive';
  }

  accountNames(accounts?: CareerRoleAllowedAccountDto[] | null): CareerRoleAllowedAccountDto[] {
    return accounts ?? [];
  }

  isCustomizingSpec(specSlug: string): boolean {
    return this.customizingSpec.has(specSlug);
  }

  startCustomizeSpec(spec: RoleSpecializationFullResponse): void {
    this.customizingSpec.add(spec.slug);
    const roleIds = this.picture?.allowedAccountIds ?? [];
    this.specAccountDrafts[spec.slug] =
      (spec.allowedAccountIds?.length ?? 0) > 0 ? [...(spec.allowedAccountIds ?? [])] : [...roleIds];
  }

  async saveRoleAccounts(): Promise<void> {
    this.actionBusy.set(true);
    try {
      const response = await firstValueFrom(
        this.api.updateCareerRoleAllowedAccounts(this.slug, { allowedAccountIds: this.roleAccountDraft })
      );
      this.snackBar.open(response.updated ? 'Role accounts updated' : 'No account changes', 'Dismiss', {
        duration: 2500
      });
      this.pictureStore.invalidate(this.slug, { refresh: true });
      await this.reload(true);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update role accounts';
      this.snackBar.open(message, 'Dismiss', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async saveSpecAccounts(specSlug: string): Promise<void> {
    this.actionBusy.set(true);
    try {
      const ids = this.specAccountDrafts[specSlug] ?? [];
      const response = await firstValueFrom(
        this.api.updateCareerRoleSpecializationAllowedAccounts(this.slug, specSlug, {
          allowedAccountIds: ids
        })
      );
      this.snackBar.open(
        response.updated ? 'Specialization accounts updated' : 'No account changes',
        'Dismiss',
        { duration: 2500 }
      );
      this.pictureStore.invalidate(this.slug, { refresh: true });
      await this.reload(true);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update specialization accounts';
      this.snackBar.open(message, 'Dismiss', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async resetSpecInherit(specSlug: string): Promise<void> {
    this.specAccountDrafts[specSlug] = [];
    this.customizingSpec.delete(specSlug);
    await this.saveSpecAccounts(specSlug);
  }

  async setActivation(active: boolean, force = false): Promise<void> {
    this.actionBusy.set(true);
    try {
      // Force on activate so status becomes migratable `active` (not `activating`).
      await this.actions.setRoleActivation(this.slug, active, active ? true : force, () => void this.reload());
    } catch {
      // snackbar in service
    } finally {
      this.actionBusy.set(false);
    }
  }

  async setSpecActivation(specSlug: string, active: boolean, force = false): Promise<void> {
    this.actionBusy.set(true);
    try {
      await this.actions.setSpecializationActivation(
        this.slug,
        specSlug,
        active,
        active ? true : force,
        () => void this.reload()
      );
      await this.reload(true);
    } finally {
      this.actionBusy.set(false);
    }
  }

  async removeSpecialization(spec: RoleSpecializationFullResponse): Promise<void> {
    if (
      !confirm(
        `Permanently remove specialization "${spec.name}" (${spec.slug})? This deletes its focus skills and cannot be undone.`
      )
    ) {
      return;
    }
    this.actionBusy.set(true);
    try {
      await this.actions.removeSpecialization(this.slug, spec.slug);
      this.pictureStore.invalidate(this.slug, { refresh: true });
      await this.reload(true);
    } catch (error: unknown) {
      this.snackBar.open(this.actions.errorMessage(error), 'Dismiss', { duration: 4000 });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async onSeniorityActivation(event: { roleSlug: string; seniority: string; active: boolean }): Promise<void> {
    this.actionBusy.set(true);
    try {
      await this.actions.setSeniorityActivation(event.roleSlug, event.seniority, event.active);
      this.pictureStore.invalidate(this.slug, { refresh: true });
    } finally {
      this.actionBusy.set(false);
    }
  }

  async runAction(action: string): Promise<void> {
    this.actionBusy.set(true);
    const done = (operationType?: string) => this.pictureStore.onBackgroundJobComplete(this.slug, operationType);
    try {
      switch (action) {
        case 'populateSkills':
          await this.actions.populateSkills(this.slug, () => done('populate_role_profile'));
          break;
        case 'populateResources':
          await this.actions.populateResources(this.slug, () => done('populate_role_resources'));
          break;
        case 'refreshAspects':
          await this.actions.refreshJobAspects(this.slug, () => done('job_aspect_refresh'));
          break;
        case 'rebalanceTaxonomy':
          await this.actions.rebalanceTaxonomy(this.slug, () => done('rebalance_role_skill_taxonomy'));
          break;
      }
    } finally {
      this.actionBusy.set(false);
    }
  }

  async addSpecialization(): Promise<void> {
    if (this.specForm.invalid) return;
    const raw = this.specForm.getRawValue();
    this.actionBusy.set(true);
    try {
      await this.actions.addSpecialization(this.slug, raw.name, raw.slug || undefined);
      this.specForm.reset({ name: '', slug: '' });
      this.pictureStore.invalidate(this.slug, { refresh: true });
      await this.reloadListItem();
      await this.reload(true);
    } finally {
      this.actionBusy.set(false);
    }
  }
}
